import { useCallback, useEffect, useState } from "react";

const OPEN_KEY = "paperclip.dataCuration.chatOpen";
const WIDTH_KEY = "paperclip.dataCuration.chatWidth";
const DEFAULT_WIDTH = 440;
export const CURATION_CHAT_MIN_WIDTH = 340;
export const CURATION_CHAT_MAX_WIDTH = 720;
const DESKTOP_QUERY = "(min-width: 1024px)";

const readStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode); the panel still works for this visit.
  }
};

const isDesktopViewport = () =>
  typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches;

const initialWidth = () => {
  const stored = Number(readStorage(WIDTH_KEY));
  return stored >= CURATION_CHAT_MIN_WIDTH && stored <= CURATION_CHAT_MAX_WIDTH
    ? stored
    : DEFAULT_WIDTH;
};

/**
 * Open state and width of the Data Curation chat. On desktop both are
 * remembered across visits. On small screens the chat is a drawer over the
 * page, so it opens only when asked and never changes the desktop preference.
 */
export const useCurationChatPanel = () => {
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport);
  const [desktopOpen, setDesktopOpen] = useState(
    () => readStorage(OPEN_KEY) !== "false",
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [width, setWidthState] = useState(initialWidth);

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY);
    const update = () => {
      setIsDesktop(media.matches);
      setMobileOpen(false);
    };
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isDesktop) {
        setMobileOpen(next);
        return;
      }
      setDesktopOpen(next);
      writeStorage(OPEN_KEY, String(next));
    },
    [isDesktop],
  );

  const setWidth = useCallback((next: number) => {
    setWidthState(next);
    writeStorage(WIDTH_KEY, String(Math.round(next)));
  }, []);

  return { open: isDesktop ? desktopOpen : mobileOpen, setOpen, width, setWidth };
};
